/**
 * Incoming-files handler — bridges the OS-level share & file-handler
 * entrypoints into our local media store.
 *
 * Two distinct OS entrypoints land here:
 *
 *   A) **share_target** — `manifest.share_target.action = "/share"` (POST,
 *      multipart). Caught in the SW (`public/sw-share-handler.js`),
 *      stashed in Cache `coach-share-target-v1`, then the navigation is
 *      redirected to `/?incoming=share`. On mount we read the cache.
 *
 *   B) **file_handlers** — `manifest.file_handlers[].action = "/open-file"`.
 *      The browser navigates the app to `/open-file` AND populates
 *      `window.launchQueue`. We register a consumer to receive
 *      `FileSystemFileHandle`s synchronously on launch.
 *
 * Both paths funnel into the same `<IncomingSheet>` UI: a glass-floating
 * modal listing the files, with "Lokal speichern" and "In Cloud speichern"
 * buttons (mirroring the ExportSheet UX so users see one mental model for
 * "files moving through the app").
 *
 * Mounted once at the App root — independent of routes so a share can
 * arrive on any deep link without breaking.
 */

import { Cloud, FolderDown, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { apiFetch } from "../../lib/api.js";
import { isLocalMediaAvailable, isTauri, saveLocalMedia } from "../../lib/local-media.js";
import { useSync } from "../../providers/sync-provider.js";

interface IncomingFile {
  /** Identifier used only for UI keying. */
  id: string;
  file: File;
}

const SHARE_CACHE = "coach-share-target-v1";
const SHARE_MANIFEST_KEY = "/__share_manifest__";

interface SharePayload {
  receivedAt: number;
  title: string | null;
  text: string | null;
  url: string | null;
  files: Array<{
    id: string;
    cacheUrl: string;
    name: string;
    type: string;
    size: number;
  }>;
}

/* ───────────────────────── public component ─────────────────────── */

export function IncomingHandler() {
  const [files, setFiles] = useState<IncomingFile[]>([]);
  const [meta, setMeta] = useState<{
    title?: string;
    text?: string;
    url?: string;
  } | null>(null);
  const [busy, setBusy] = useState<null | "local" | "cloud">(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { db, orgId } = useSync();

  /* ─── A) share_target ─── */
  useEffect(() => {
    if (isTauri()) return;
    const search = new URLSearchParams(location.search);
    if (search.get("incoming") !== "share") return;

    let cancelled = false;
    (async () => {
      try {
        const cache = await caches.open(SHARE_CACHE);
        const manifestRes = await cache.match(SHARE_MANIFEST_KEY);
        if (!manifestRes) {
          // Either the SW didn't run or already cleared it — just strip
          // the query param so we don't loop.
          stripIncomingParam(navigate, location);
          return;
        }
        const payload = (await manifestRes.json()) as SharePayload;
        const reconstructed: IncomingFile[] = [];
        for (const entry of payload.files) {
          const res = await cache.match(entry.cacheUrl);
          if (!res) continue;
          const blob = await res.blob();
          reconstructed.push({
            id: entry.id,
            file: new File([blob], entry.name, { type: entry.type }),
          });
        }
        if (cancelled) return;
        setFiles(reconstructed);
        setMeta({
          title: payload.title ?? undefined,
          text: payload.text ?? undefined,
          url: payload.url ?? undefined,
        });
      } catch (e) {
        console.error("[incoming] share read failed", e);
        if (!cancelled) setError(friendly(e, "Empfangen"));
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-run if the user navigates between deep links carrying ?incoming=share.
  }, [location, navigate]);

  /* ─── B) file_handlers via launchQueue ─── */
  useEffect(() => {
    if (isTauri()) return;
    const lq = (window as unknown as { launchQueue?: LaunchQueue }).launchQueue;
    if (!lq) return;
    lq.setConsumer(async (params: LaunchParams) => {
      if (!params.files || params.files.length === 0) return;
      try {
        const collected: IncomingFile[] = [];
        for (const handle of params.files) {
          // `handle` is a FileSystemFileHandle; .getFile() returns a File.
          const file = await handle.getFile();
          collected.push({
            id: `lh-${handle.name}-${file.lastModified}`,
            file,
          });
        }
        setFiles(collected);
        setMeta(null);
      } catch (e) {
        console.error("[incoming] launchQueue read failed", e);
        setError(friendly(e, "Datei \u00f6ffnen"));
      }
    });
  }, []);

  /* ─── actions ─── */

  const close = useCallback(async () => {
    setFiles([]);
    setMeta(null);
    setError(null);
    // Drop the cache so the next share starts clean.
    try {
      const cache = await caches.open(SHARE_CACHE);
      for (const k of await cache.keys()) await cache.delete(k);
    } catch {
      /* non-fatal */
    }
    stripIncomingParam(navigate, location);
  }, [navigate, location]);

  const saveLocal = useCallback(async () => {
    if (!db || !orgId) {
      setError("Lokaler Speicher ist noch nicht bereit.");
      return;
    }
    if (!isLocalMediaAvailable()) {
      setError("Auf diesem Ger\u00e4t ist kein lokaler Medienspeicher verf\u00fcgbar.");
      return;
    }
    setBusy("local");
    setError(null);
    try {
      for (const { file } of files) {
        await saveLocalMedia(db, orgId, file);
      }
      await close();
      navigate("/media");
    } catch (e) {
      setError(friendly(e, "Lokal speichern"));
    } finally {
      setBusy(null);
    }
  }, [db, orgId, files, close, navigate]);

  const saveCloud = useCallback(async () => {
    setBusy("cloud");
    setError(null);
    try {
      for (const { file } of files) {
        const bytes = new Uint8Array(await file.arrayBuffer());
        await uploadToCloud({
          bytes,
          mime: file.type || "application/octet-stream",
          filename: file.name,
        });
      }
      await close();
      navigate("/media");
    } catch (e) {
      setError(friendly(e, "Cloud-Upload"));
    } finally {
      setBusy(null);
    }
  }, [files, close, navigate]);

  if (files.length === 0 && !error) return null;

  return (
    <div
      className="fixed inset-0 z-295 flex items-end justify-center bg-black/60 px-4 backdrop-blur-sm sm:items-center"
      role="presentation"
      onClick={busy ? undefined : () => void close()}
    >
      <div
        className="glass-floating w-full max-w-md rounded-3xl border border-white/10 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="incoming-title"
        style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 id="incoming-title" className="text-body-md font-semibold leading-tight">
              Dateien empfangen
            </h2>
            <p className="mt-1 text-[12px]" style={{ color: "var(--color-on-surface-variant)" }}>
              {files.length === 1 ? "Wohin damit?" : `${files.length} Dateien — wohin damit?`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void close()}
            disabled={busy !== null}
            aria-label="Schlie\u00dfen"
            className="rounded-lg p-1.5 text-white/70 hover:bg-white/10 disabled:opacity-50"
          >
            <X className="size-4" />
          </button>
        </div>

        {meta?.title || meta?.text || meta?.url ? (
          <div
            className="mb-3 rounded-xl border border-white/5 bg-white/2 p-3 text-[12px]"
            style={{ color: "var(--color-on-surface-variant)" }}
          >
            {meta.title && <div className="font-semibold text-white">{meta.title}</div>}
            {meta.text && <div className="mt-1 line-clamp-3">{meta.text}</div>}
            {meta.url && <div className="mt-1 truncate text-pitch-lime">{meta.url}</div>}
          </div>
        ) : null}

        <ul className="mb-4 max-h-48 space-y-1.5 overflow-y-auto pr-1">
          {files.map(({ id, file }) => (
            <li
              key={id}
              className="flex items-center justify-between gap-3 rounded-lg bg-white/3 px-3 py-2 text-[12px]"
            >
              <span className="truncate font-medium" title={file.name}>
                {file.name}
              </span>
              <span
                className="shrink-0 tabular-nums"
                style={{ color: "var(--color-on-surface-variant)" }}
              >
                {formatBytes(file.size)}
              </span>
            </li>
          ))}
        </ul>

        {error && (
          <div
            className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 p-2 text-[12px] text-red-200"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => void saveLocal()}
            disabled={busy !== null}
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-white/10 px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-white/15 disabled:opacity-50"
          >
            {busy === "local" ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <FolderDown className="size-4" />
            )}
            Lokal speichern
          </button>
          <button
            type="button"
            onClick={() => void saveCloud()}
            disabled={busy !== null}
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-pitch-lime px-4 py-2.5 text-[13px] font-semibold text-[#1d3700] hover:shadow-glow disabled:opacity-50"
          >
            {busy === "cloud" ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Cloud className="size-4" />
            )}
            In Cloud speichern
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── helpers ─────────────────────────────── */

function stripIncomingParam(
  navigate: ReturnType<typeof useNavigate>,
  location: ReturnType<typeof useLocation>,
) {
  const search = new URLSearchParams(location.search);
  if (!search.has("incoming")) return;
  search.delete("incoming");
  const qs = search.toString();
  navigate(
    {
      pathname: location.pathname === "/share" ? "/" : location.pathname,
      search: qs ? `?${qs}` : "",
    },
    { replace: true },
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function friendly(err: unknown, action: string): string {
  const msg = err instanceof Error ? err.message : String(err);
  return `${action} fehlgeschlagen: ${msg}`;
}

interface PresignResponse {
  url: string;
  s3Key: string;
  headers: Record<string, string>;
}

async function uploadToCloud(args: {
  bytes: Uint8Array;
  mime: string;
  filename: string;
}): Promise<void> {
  const presign = await apiFetch<PresignResponse>("/api/v1/media/upload-url", {
    method: "POST",
    body: JSON.stringify({ mime: args.mime, bytes: args.bytes.byteLength }),
  });
  // Detach from any SharedArrayBuffer backing — fetch refuses those.
  const buf = new Uint8Array(args.bytes.byteLength);
  buf.set(args.bytes);
  const put = await fetch(presign.url, {
    method: "PUT",
    headers: presign.headers,
    body: buf,
  });
  if (!put.ok) {
    const body = await put.text().catch(() => "");
    throw new Error(`Upload ${put.status}. ${body}`.trim());
  }
  await apiFetch("/api/v1/media/register", {
    method: "POST",
    body: JSON.stringify({
      s3Key: presign.s3Key,
      mime: args.mime,
      bytes: args.bytes.byteLength,
      filename: args.filename.replace(/\.[^./\\]+$/, ""),
    }),
  });
}

/* ─── launchQueue typings (not yet in lib.dom for all TS versions) ── */

interface LaunchParams {
  readonly files?: ReadonlyArray<FileSystemFileHandle>;
}
interface LaunchQueue {
  setConsumer(consumer: (params: LaunchParams) => void | Promise<void>): void;
}
